/** Shared bound for every roster writer, including accepted pickups. */
export const MAX_ROSTER = 40;
// Shared between worker (DOs), and src (UI). No runtime deps.

/**
 * A field is tournament data, not a constant: events run on one field or on
 * six, and the names are rarely "1" and "2". `FieldId` is therefore a plain
 * string validated at runtime against the configured list: a compile-time
 * union cannot describe a set the organiser edits on the day.
 *
 * The id is also the Durable Object name (`field-<id>`), so it must be stable
 * and URL-safe: it is generated once from the name and never rewritten.
 */
export type FieldId = string;

export interface Field {
  id: FieldId;
  /** What staff call it: "Field 1", "Show Field", "Pitch B". */
  name: string;
  /** Display order. Lower first. */
  order: number;
  /**
   * Where this pitch's stream is watched - the link the board and the
   * calendar hand a spectator. Best set to a channel's PERMANENT live URL
   * (youtube.com/@channel/live), which follows whatever that channel is
   * streaming now: set once, never edited per game. Absent = not streamed.
   */
  streamUrl?: string;
}

/** Used until an organiser configures their own, and to keep existing
 *  deployments (whose DOs are named field-1 / field-2) working untouched. */
export const DEFAULT_FIELDS: Field[] = [
  { id: "1", name: "Field 1", order: 0 },
  { id: "2", name: "Field 2", order: 1 },
];

/** Ids must survive being put in a URL and a DO name. */
export function isFieldId(v: string): boolean {
  return /^[a-zA-Z0-9_-]{1,32}$/.test(v);
}

/** Slug for a new field, derived from its name and kept unique. */
export function fieldIdFrom(name: string, taken: readonly FieldId[]): FieldId {
  const base =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      // "Field 1" would slug to "field-1", which reads as the DO name of the
      // field whose id is "1". Strip the prefix so ids stay unambiguous.
      .replace(/^field-?/, "")
      .replace(/^-+/, "")
      .slice(0, 24) || "field";
  if (!taken.includes(base)) return base;
  for (let n = 2; n < 100; n += 1) {
    const candidate = `${base}-${n}`;
    if (!taken.includes(candidate)) return candidate;
  }
  return `${base}-${Date.now()}`;
}

export function fieldLabel(fields: Field[], id: FieldId): string {
  return fields.find((f) => f.id === id)?.name ?? `Field ${id}`;
}

// ---------- Divisions ----------

/**
 * A division is just a named bucket of teams that share a standings table:
 * open, women, mixed, juniors: whatever the organiser types. Deliberately
 * NOT an enum: this app has to serve any tournament, and the set of divisions
 * differs per event.
 *
 * Teams without a division still work (`Team.divisionId` is optional), so a
 * single-division tournament never has to touch this screen.
 */
export interface Division {
  id: string;
  name: string;
  /** Short tag for the board and the seed labels, e.g. "OPEN" / "W". 1–6 chars. */
  short: string;
  /** Display order on the board. Lower first. */
  order: number;
}

export const UNASSIGNED_DIVISION = "__none__";
export const UNASSIGNED_POOL = "__nopool__";

/**
 * A pool is a sub-group INSIDE one division that gets its own standings table:
 * "Pool A" and "Pool B" of the open division. Optional throughout: a
 * division with no pools stays one flat table, which is the common case here.
 *
 * Pools belong to a division, so two divisions may both have a "Pool A"
 * without colliding.
 */
export interface Pool {
  id: string;
  divisionId: string;
  /** "Pool A", "Group 1". */
  name: string;
  order: number;
}

export function poolLabel(pools: Pool[], id: string | undefined): string {
  if (id === undefined) return "No pool";
  return pools.find((p) => p.id === id)?.name ?? "No pool";
}

export function poolsOf(pools: Pool[], divisionId: string): Pool[] {
  return pools.filter((p) => p.divisionId === divisionId).sort((a, b) => a.order - b.order);
}

export function divisionLabel(divisions: Division[], id: string | undefined): string {
  if (id === undefined) return "No division";
  return divisions.find((d) => d.id === id)?.name ?? "No division";
}

// ---------- Teams ----------

export interface Player {
  id: string;
  name: string;
  /** Jersey number as text: "7", "00" and "" are all real cases. */
  number: string;
  isCaptain: boolean;
  /**
   * On this EVENT roster as a pickup, not as one of the club's players
   * (2026-09-15). Set when a pickup is accepted, and by whoever edits the
   * roster (an event that ran before accounts typed its pickups in by name).
   * Presentation and the library sync only: never a permission, never
   * evidence of a club seat. Absent means no; only `true` is stored.
   */
  pickup?: boolean;
}

/**
 * Ceiling on a stored crest, in data-URL characters (~110 kB). The real limit
 * behind it is the Durable Object per-value cap: a logo lives inline on the
 * team record and is copied into every game-state snapshot. SHARED so the
 * upload path can refuse (and name the crest) before the request instead of
 * the server refusing with a sentence that blames the roster.
 */
export const MAX_LOGO_CHARS = 150_000;

export interface Team {
  id: string;
  name: string;
  /** Short code shown on the scorebug, e.g. "KYV". 2–4 chars. */
  short: string;
  /** CSS color for the team strip on the overlay. */
  color: string;
  /** Small data-URL image (client resizes to ≤256px before upload — the
   *  pregame intro card is the render that needed more than the old 96). */
  logo?: string;
  /**
   * Optional variant of the crest for DARK grounds (the final board's band,
   * the intro card). `logo` stays the primary and must read on light: the
   * board, the hub and the scorebug's own team-colour panel are all light
   * surfaces. One team's peach wordmark was invisible twice over - on its own
   * peach panel with the peach file, on the final band with the black one -
   * and no single file can win both; recolouring procedurally (mask knockout)
   * flattened multi-colour crests, so the variant is explicit art, not magic.
   */
  logoDark?: string;
  /** Optional: teams can play without a roster entered. */
  roster?: Player[];
  /**
   * The club this squad belongs to. New squads can have their own ids;
   * historical teams may share their club's id. Ordinary team edits preserve
   * this ownership rather than changing or clearing it.
   */
  clubId?: string;
  /** When the library copy was taken. The tournament row is its own truth
   *  from then on; nothing syncs back without an explicit action (D11). */
  library?: { importedAt: number };
  /** Which division's standings this team appears in. Absent = unassigned. */
  divisionId?: string;
  /** Pool within that division. Absent = the division has no pools, or this
   *  team has not been drawn into one yet. */
  poolId?: string;
}

export function playerLabel(p: Player): string {
  return p.number === "" ? p.name : `#${p.number} ${p.name}`;
}

// ---------- A roster row's limits ----------

/**
 * What one roster row may carry, the Worker's `parseRoster` rule: a name of
 * 1-40 characters (UTF-16 units, as `str()` and `maxLength` count them) and a
 * number of at most 3. `parseRoster` refuses the WHOLE save for one row past
 * either, so every other way a row reaches a library or an event roster keeps
 * to them too (2026-09-14): a pickup's Telegram name or an application's
 * "1234" used to land on a roster and block every save of it after.
 */
export const MAX_PLAYER_NAME = 40;
export const MAX_PLAYER_NUMBER = 3;

/**
 * A jersey number someone TYPED, as a roster keeps it: digits, at most three,
 * "" for none. A leading "#" and surrounding spaces are forgiven, because an
 * older cached form let both through; anything else is null, for a validator
 * to refuse while the person is still there to fix it.
 */
export function typedJerseyNumber(raw: string): string | null {
  const number = raw.trim().replace(/^#\s*/, "");
  return /^\d*$/.test(number) && number.length <= MAX_PLAYER_NUMBER ? number : null;
}

/** What a jersey field keeps while it is typed into: digits, three at most. */
export function jerseyInput(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, MAX_PLAYER_NUMBER);
}

/**
 * A number COPIED onto a roster from somewhere that never checked it: a
 * stored library row, a request made before the check. A clean number stays;
 * otherwise its first run of digits when that run fits ("81 (8)" -> "81",
 * "No 7" -> "7"), else none: "1234" becomes "" rather than a wrong "123".
 */
export function clampJerseyNumber(raw: string): string {
  const typed = typedJerseyNumber(raw);
  if (typed !== null) return typed;
  const run = /\d+/.exec(raw)?.[0] ?? "";
  return run.length <= MAX_PLAYER_NUMBER ? run : "";
}

/**
 * A name copied onto a roster from somewhere with a looser limit - an
 * account's label (60, and a Telegram profile's first and last name have no
 * cap here at all) - trimmed and cut to 40 without splitting a character.
 * Inner spacing is left alone: a copy is not a rename.
 */
export function clampRosterName(raw: string): string {
  const name = raw.trim();
  if (name.length <= MAX_PLAYER_NAME) return name;
  let cut = "";
  for (const char of name) {
    if (cut.length + char.length > MAX_PLAYER_NAME) break;
    cut += char;
  }
  return cut.trim();
}

/** A row copied onto a roster, inside the limits; the same object when it already was. */
export function clampRosterRow(p: Player): Player {
  const name = clampRosterName(p.name);
  const number = clampJerseyNumber(p.number);
  return name === p.name && number === p.number ? p : { ...p, name, number };
}

/** A team copied whole (a stored entry accepted, a tournament cloned) with its
 *  roster inside the limits; a team with no roster stays as it is. */
export function clampTeamRoster<T extends { roster?: Player[] }>(team: T): T {
  return team.roster === undefined ? team : { ...team, roster: team.roster.map(clampRosterRow) };
}

// ---------- Slots ----------

/**
 * Who plays in one side of a game. A bracket exists because a game can be
 * scheduled before its participants are known:
 *
 *   team        - a concrete team, known now (all of pool play).
 *   seed        - "2nd in the open division", resolved from standings.
 *   winner/loser - the outcome of another scheduled game.
 *
 * Resolution is LAZY and never written back: `resolveSlot` recomputes from
 * live standings and results every render, so a corrected pool score
 * re-seeds the bracket automatically instead of stranding a stale team id.
 */
export type Slot =
  | { kind: "team"; teamId: string }
  /** `poolId` narrows the seed to one pool: "2nd in Pool B" rather than
   *  "2nd in the division", which is what a crossover bracket needs. */
  | { kind: "seed"; divisionId: string; rank: number; poolId?: string }
  | { kind: "winner"; gameId: string }
  | { kind: "loser"; gameId: string };

export function teamSlot(teamId: string): Slot {
  return { kind: "team", teamId };
}

/**
 * What a GENERATED game is, structurally ("the second semifinal", "round 1 of
 * this pool") as opposed to `note`, which is the free text an organiser sees
 * and edits.
 *
 * It exists because `note` is PERSISTED DATA and must never carry a language.
 * A tournament generated by an organiser working in English has to read
 * correctly for a Ukrainian spectator on the board, and the other way round,
 * which rules out translating at generation time. So the generator writes both:
 * the canonical English `note` it always wrote, and this.
 *
 * The rendering rule is in `gameNoteOf()` (`src/i18n/labels.ts`): translate
 * only while `note` still matches `canonicalNote(label)`. The moment an
 * organiser retypes the note it stops matching, and from then on it is user
 * content and is shown verbatim in both languages, which is exactly right,
 * because "Показовий матч" is not a thing this app gets to translate.
 *
 * Optional throughout: games created before it existed, and every game an
 * organiser adds by hand, simply have no label.
 */
export type GameLabel =
  | { kind: "final" }
  | { kind: "semifinal"; n: number }
  | { kind: "quarterfinal"; n: number }
  /** `teams` is the bracket width the round is named after: "Round of 16". */
  | { kind: "roundOf"; teams: number; n: number }
  | { kind: "thirdPlace" }
  | { kind: "grandFinal"; ifNecessary?: boolean }
  | { kind: "losersFinal" }
  | { kind: "losersSemifinal" }
  | { kind: "losersRound"; n: number; i?: number }
  /** Wraps the round's own label: "WB Semifinal 1". */
  | { kind: "winnersBracket"; inner: GameLabel }
  /** `pool` is the pool's NAME, which is user content and is never translated;
   *  absent means the division has no pools and the word "Pool" is generic. */
  | { kind: "poolRound"; pool?: string; n: number }
  /**
   * The deciding game of a consolation chain: "5th place". `place` is the
   * finish its WINNER takes, so a chain off four quarterfinals emits 5 and 7.
   * `thirdPlace` above is the same idea and predates this: it stays exactly as
   * it is, so every schedule already storing "3rd place" keeps its label.
   */
  | { kind: "placement"; place: number }
  /** A round INSIDE that chain, wrapping the round's own label the way
   *  `winnersBracket` does: "5th place Semifinal 1". */
  | { kind: "placementRound"; place: number; inner: GameLabel }
  /** Swiss pairings are drawn one round at a time from the live standings, so
   *  the round number is the only structure there is to record. */
  | { kind: "swissRound"; n: number };

/**
 * The canonical ENGLISH note for a label: byte-for-byte what the generator has
 * always written into `ScheduledGame.note`.
 *
 * Lives here rather than in `generate.ts` so the board can compare a note
 * against it without dragging six hundred lines of bracket generators into the
 * main chunk.
 */
export function canonicalNote(label: GameLabel): string {
  switch (label.kind) {
    case "final":
      return "Final";
    case "semifinal":
      return `Semifinal ${label.n}`;
    case "quarterfinal":
      return `Quarterfinal ${label.n}`;
    case "roundOf":
      return `Round of ${label.teams} - ${label.n}`;
    case "thirdPlace":
      return "3rd place";
    case "grandFinal":
      return label.ifNecessary === true ? "Grand final (if necessary)" : "Grand final";
    case "losersFinal":
      return "Losers final";
    case "losersSemifinal":
      return "Losers semifinal";
    case "losersRound":
      return label.i === undefined ? `Losers R${label.n}` : `Losers R${label.n} ${label.i}`;
    case "winnersBracket":
      return `WB ${canonicalNote(label.inner)}`;
    case "poolRound":
      return `${label.pool ?? "Pool"} R${label.n}`;
    case "placement":
      return `${englishOrdinal(label.place)} place`;
    case "placementRound":
      return `${englishOrdinal(label.place)} place ${canonicalNote(label.inner)}`;
    case "swissRound":
      return `Swiss R${label.n}`;
  }
}

/**
 * English ordinal for a finishing place: 1st, 2nd, 3rd, 4th, 11th, 21st.
 *
 * Here because this builds the CANONICAL STORED NOTE, which is English like
 * everything else in `shared/`. Exported so `src/i18n/format.ts` renders the
 * English side of a placement label through this same function rather than a
 * second copy of the rule; its Ukrainian side is its own, because Ukrainian
 * ordinals do not follow the last digit alone ("7-ме" against "5-те").
 */
export function englishOrdinal(n: number): string {
  const last = n % 10;
  const teen = n % 100;
  if (last === 1 && teen !== 11) return `${n}st`;
  if (last === 2 && teen !== 12) return `${n}nd`;
  if (last === 3 && teen !== 13) return `${n}rd`;
  return `${n}th`;
}

/**
 * The inverse: read a canonical note back into the label that produced it.
 *
 * `label` only exists on games generated since it was added, and a tournament
 * that was drawn before that has a schedule full of "Semifinal 1" with nothing
 * structural behind it. Recognising those is what lets an existing event read
 * in Ukrainian instead of staying English forever.
 *
 * It is safe because the caller round-trips it: `gameNoteOf` translates only
 * when `canonicalNote(parsed)` reproduces the stored note exactly, so a note an
 * organiser typed themselves ("Показовий матч", "Final (rescheduled)") never
 * matches and is shown as written.
 */
export function parseCanonicalNote(note: string | undefined): GameLabel | undefined {
  if (note === undefined) return undefined;
  const text = note.replace(/[\u2012-\u2015]/g, "-").trim();

  const wb = /^WB (.+)$/.exec(text);
  if (wb?.[1] !== undefined) {
    const inner = parseCanonicalNote(wb[1]);
    return inner === undefined ? undefined : { kind: "winnersBracket", inner };
  }
  if (text === "Final") return { kind: "final" };
  if (text === "3rd place") return { kind: "thirdPlace" };
  if (text === "Grand final") return { kind: "grandFinal" };
  if (text === "Grand final (if necessary)") return { kind: "grandFinal", ifNecessary: true };
  if (text === "Losers final") return { kind: "losersFinal" };
  if (text === "Losers semifinal") return { kind: "losersSemifinal" };

  const semi = /^Semifinal (\d+)$/.exec(text);
  if (semi?.[1] !== undefined) return { kind: "semifinal", n: Number(semi[1]) };
  const quarter = /^Quarterfinal (\d+)$/.exec(text);
  if (quarter?.[1] !== undefined) return { kind: "quarterfinal", n: Number(quarter[1]) };
  const round = /^Round of (\d+) - (\d+)$/.exec(text);
  if (round?.[1] !== undefined && round[2] !== undefined) {
    return { kind: "roundOf", teams: Number(round[1]), n: Number(round[2]) };
  }
  const losers = /^Losers R(\d+)(?: (\d+))?$/.exec(text);
  if (losers?.[1] !== undefined) {
    const n = Number(losers[1]);
    return losers[2] === undefined
      ? { kind: "losersRound", n }
      : { kind: "losersRound", n, i: Number(losers[2]) };
  }
  const swiss = /^Swiss R(\d+)$/.exec(text);
  if (swiss?.[1] !== undefined) return { kind: "swissRound", n: Number(swiss[1]) };
  // "5th place", and "5th place Semifinal 1" for a round inside the chain.
  // Before the pool fallback, which would otherwise read the whole thing as a
  // pool called "5th place Semifinal". "3rd place" never reaches here: it is
  // answered above and keeps the older `thirdPlace` kind it is stored with.
  const placement = /^(\d+(?:st|nd|rd|th)) place(?: (.+))?$/.exec(text);
  if (placement?.[1] !== undefined) {
    const place = Number.parseInt(placement[1], 10);
    // Only OUR ordinal counts. "1th place" is somebody's own note, and reading
    // it as generated would translate a sentence an organiser typed.
    if (englishOrdinal(place) === placement[1]) {
      if (placement[2] === undefined) return { kind: "placement", place };
      const inner = parseCanonicalNote(placement[2]);
      if (inner !== undefined) return { kind: "placementRound", place, inner };
    }
  }
  // Anything else ending in " R<n>" is a pool round; the part before it is the
  // pool's own name, which is user content either way.
  const pool = /^(.+) R(\d+)$/.exec(text);
  if (pool?.[1] !== undefined && pool[2] !== undefined) {
    const n = Number(pool[2]);
    return pool[1] === "Pool" ? { kind: "poolRound", n } : { kind: "poolRound", pool: pool[1], n };
  }
  return undefined;
}

/**
 * Is this note still the one the generator wrote?
 *
 * Dashes are normalised before comparing, and that is not cosmetic: "Round of
 * 16" used to be generated with an em dash and is stored that way in every
 * tournament created before the dash cleanup. Comparing raw would quietly stop
 * translating those rows.
 */
export function isGeneratedNote(note: string | undefined, label: GameLabel | undefined): boolean {
  if (note === undefined || label === undefined) return false;
  return normaliseNote(note) === normaliseNote(canonicalNote(label));
}

/**
 * The label that may SPEAK for a game's note, or undefined when the note is
 * the organiser's own words. ONE implementation for the board's translation
 * and the result card's round line, which had grown their own copies of half
 * of it.
 *
 * The stored label wins while the note is still the text it generated. It can
 * go STALE: a generated game repurposed by hand keeps its old label — a real
 * event's "no time for a bronze match" semifinal, renamed "3rd place" because
 * its loser takes bronze, still carried `semifinal` — and testing only the
 * stored label left that note untranslated on every surface. So when it no
 * longer matches, the note itself is read back: verbatim generator text means
 * the organiser chose words every dictionary already knows. Anything else is
 * user content, and the caller shows it exactly as written.
 */
export function effectiveLabel(
  note: string | undefined,
  label: GameLabel | undefined,
): GameLabel | undefined {
  if (label !== undefined && isGeneratedNote(note, label)) return label;
  const parsed = parseCanonicalNote(note);
  return parsed !== undefined && isGeneratedNote(note, parsed) ? parsed : undefined;
}

/**
 * The games a pool rename must rewrite, already rewritten.
 *
 * Everything else holds a pool by id and reads its name at render time; a
 * generated round robin is the exception, because the generator wrote the
 * name INTO the game ("A R1", `label.pool: "A"`). An organiser who draws
 * pools "A" and "B", generates, then renames them "Pool A" and "Pool B" would
 * otherwise keep a schedule saying «A, тур 1». Only notes that are still the
 * generator's own text for the OLD name follow: a note the organiser retyped
 * is their words (see `effectiveLabel`), and a game moved in from another
 * pool keeps the name it was generated with.
 */
export function renamePoolInGames(
  games: readonly ScheduledGame[],
  poolId: string,
  from: string,
  to: string,
): ScheduledGame[] {
  if (from === to) return [];
  return games.flatMap((game) => {
    if (game.poolId !== poolId) return [];
    const label = effectiveLabel(game.note, game.label);
    if (label?.kind !== "poolRound" || label.pool !== from) return [];
    const next: GameLabel = { ...label, pool: to };
    return [{ ...game, note: canonicalNote(next), label: next }];
  });
}

function normaliseNote(s: string): string {
  return s.replace(/[\u2012-\u2015]/g, "-").replace(/\s+/g, " ").trim().toLowerCase();
}

export interface ScheduledGame {
  id: string;
  fieldId: FieldId;
  /** ISO datetime, local tournament time. */
  startAt: string;
  /**
   * Participants. Optional only so games stored before slots existed still
   * parse: `gameSlots()` falls back to the legacy team ids below.
   */
  slotA?: Slot;
  slotB?: Slot;
  /** @deprecated Superseded by `slotA`. Retained so old rows keep working. */
  teamAId?: string;
  /** @deprecated Superseded by `slotB`. Retained so old rows keep working. */
  teamBId?: string;
  /** Free-form label, e.g. "Pool A" or "Semifinal". USER CONTENT once it has
   *  been edited: see `label`. */
  note?: string;
  /** What the generator meant, structurally, so the note can be rendered in
   *  either language without a locale ever being stored. See `GameLabel`. */
  label?: GameLabel;
  /** Which division's schedule and standings this game belongs to. */
  divisionId?: string;
  /** Pool this game counts towards, when the division has pools. */
  poolId?: string;
  /**
   * Pool play feeds the seeding; bracket games consume it. Kept apart so a
   * bracket result can never re-seed the bracket that produced it. Absent is
   * treated as "pool".
   */
  stage?: "pool" | "bracket";
  /** Depth within the bracket (1 = first round). Groups columns on the board
   *  when the game has no explicit canvas position. */
  bracketRound?: number;
  /**
   * Canvas position, set by dragging in the bracket editor. When present the
   * public board mirrors this layout instead of auto-grouping by round, so
   * what the organiser draws is what spectators see.
   */
  x?: number;
  y?: number;
  /** Filled by FieldDO when the live game reaches "final". */
  result?: { scoreA: number; scoreB: number };
  /**
   * This one game's own length and format, where it does not follow its
   * division. A late train, a 60-minute make-up game to 11 squeezed into the
   * evening: the schedule, the checker, the ICS feed and the scorekeeper's
   * prefill all read it through `playMinutes` / `formatForGame`, so one
   * number typed here is the number everywhere.
   */
  override?: GameOverride;
}

/**
 * A game's own format. A FULL snapshot, exactly like `DivisionFormat` in
 * `shared/settings.ts`: the dialog seeds every field from what the game
 * would play today when the toggle goes on, so an absent cap here is a real
 * answer ("this game calls no cap"), never "inherit". Inherit is spelled by
 * the whole object being absent.
 */
export interface GameOverride {
  /** Playing minutes, halftime included: what `playMinutes` returns. */
  minutes: number;
  /** Game-to-N points; null = time-only. */
  scoreTarget: number | null;
  timeoutsPerHalf: number;
  softCapMin?: number;
  hardCapMin?: number;
}

/** Slots for a game, upgrading legacy `teamAId`/`teamBId` rows on read. */
export function gameSlots(game: ScheduledGame): { a: Slot | null; b: Slot | null } {
  const a = game.slotA ?? (game.teamAId !== undefined ? teamSlot(game.teamAId) : null);
  const b = game.slotB ?? (game.teamBId !== undefined ? teamSlot(game.teamBId) : null);
  return { a, b };
}

/** The concrete team on one side, or null while the slot is still unresolved. */
export function resolveSlot(
  slot: Slot | null,
  ctx: {
    teams: Team[];
    schedule: ScheduledGame[];
    seedsOf: (divisionId: string, poolId?: string) => string[];
  },
  depth = 0,
): Team | null {
  if (slot === null) return null;
  // A malformed bracket (game X fed by game X) must not hang the board.
  if (depth > 16) return null;

  switch (slot.kind) {
    case "team":
      return ctx.teams.find((t) => t.id === slot.teamId) ?? null;
    case "seed": {
      const id = ctx.seedsOf(slot.divisionId, slot.poolId)[slot.rank - 1];
      return id === undefined ? null : (ctx.teams.find((t) => t.id === id) ?? null);
    }
    case "winner":
    case "loser": {
      const game = ctx.schedule.find((g) => g.id === slot.gameId);
      if (game?.result === undefined) return null;
      const { a, b } = gameSlots(game);
      const { scoreA, scoreB } = game.result;
      if (scoreA === scoreB) return null; // a draw decides nothing
      const wantA = slot.kind === "winner" ? scoreA > scoreB : scoreA < scoreB;
      return resolveSlot(wantA ? a : b, ctx, depth + 1);
    }
  }
}

export type LabelCtx = {
  teams: Team[];
  divisions: Division[];
  schedule: ScheduledGame[];
  pools?: Pool[];
};

/**
 * What a slot IS before it resolves, with the words left out.
 *
 * The logic (which pool, which rank, which feeding game) has exactly one
 * implementation, and the two formatters below it disagree about nothing but
 * language: `slotLabel()` writes English for the OBS overlay and the CSV
 * export, `slotTextOf()` (`src/i18n/labels.ts`) writes whatever the reader
 * chose. Splitting the FORMAT out rather than duplicating the lookup is what
 * keeps a rewired bracket from reading differently on the stream and the board.
 */
export type SlotDescriptor =
  | { kind: "tbd" }
  | { kind: "team"; name: string }
  /** `group` is a pool name ("Pool A") or a division code ("OPEN"), or null
   *  when the division is gone. Both are user content. */
  | { kind: "seed"; group: string | null; rank: number }
  | { kind: "outcome"; won: boolean; note?: string; label?: GameLabel };

export function slotDescriptor(slot: Slot | null, ctx: LabelCtx): SlotDescriptor {
  if (slot === null) return { kind: "tbd" };
  switch (slot.kind) {
    case "team": {
      const name = ctx.teams.find((t) => t.id === slot.teamId)?.name;
      return name === undefined ? { kind: "tbd" } : { kind: "team", name };
    }
    case "seed": {
      // A pool seed reads "A2" the way a schedule sheet writes it; a division
      // seed keeps the division code, "OPEN 2".
      if (slot.poolId !== undefined) {
        const pool = (ctx.pools ?? []).find((p) => p.id === slot.poolId);
        if (pool !== undefined) return { kind: "seed", group: pool.name, rank: slot.rank };
      }
      const div = ctx.divisions.find((d) => d.id === slot.divisionId);
      return { kind: "seed", group: div?.short ?? null, rank: slot.rank };
    }
    case "winner":
    case "loser": {
      const game = ctx.schedule.find((g) => g.id === slot.gameId);
      return {
        kind: "outcome",
        won: slot.kind === "winner",
        ...(game?.note !== undefined ? { note: game.note } : {}),
        ...(game?.label !== undefined ? { label: game.label } : {}),
      };
    }
  }
}

/**
 * What to show before a slot resolves: "OPEN 2", "Winner SF1", in ENGLISH.
 *
 * Still the right function for the OBS overlay (broadcast graphics, English
 * permanently), and for the CSV exports. Every other surface goes through
 * `slotTextOf()` so a Ukrainian spectator reads «Переможець Півфінал 1».
 */
export function slotLabel(slot: Slot | null, ctx: LabelCtx): string {
  const d = slotDescriptor(slot, ctx);
  switch (d.kind) {
    case "tbd":
      return "TBD";
    case "team":
      return d.name;
    case "seed":
      return `${d.group ?? "?"} ${d.rank}`;
    case "outcome":
      return `${d.won ? "Winner" : "Loser"} ${d.note ?? "game"}`;
  }
}

// ---------- Live game ----------

/**
 * Wall-clock model: display time = accumulatedMs + (running ? now - startedAt : 0).
 * Clients render locally at 60fps from this: ticks are never synced.
 * All timestamps are SERVER time; clients correct with the `now` field of
 * each WS/state message (skew = serverNow - clientNow).
 */
export interface ClockState {
  running: boolean;
  startedAt: number | null;
  accumulatedMs: number;
}

export interface GameConfig {
  /**
   * countdown = the bug shows time LEFT in the game; countup = time played.
   * Either way it is ONE clock for the whole game, not one per half: see
   * `lengthMin` and `gameElapsedMs`. Countdown is the default because that is
   * what a sideline reads ("twenty minutes left"), and it is what the caps are
   * counted against.
   */
  clockMode: "countdown" | "countup";
  /**
   * Playing minutes for the WHOLE game: where the countdown starts. One
   * number, deliberately not a half. The clock runs from the opening pull to
   * the final whistle and only PAUSES at halftime, resuming where it stopped
   * when the second half starts - so an 80-minute game reads 80:00 down to
   * 0:00 however unevenly the break falls, and neither half "gets" forty
   * minutes of its own. (It used to: the clock restarted at the break, which
   * is what `priorMs` was for.) Games here end on a score or a called cap,
   * never on 0:00 - the clock is a reference for the sideline, not a rule.
   */
  lengthMin: number;
  /**
   * COMPATIBILITY, and a lie by name. Before 2026-09-05 this was minutes per
   * half and the clock restarted at the break; a client on that build shows
   * `halfLengthMin * 60_000 - clock`, and with no such field it showed
   * "NaN:NaN" - on stream, mid-game, from an OBS Browser Source that had not
   * been reloaded since the deploy (the service worker does not skipWaiting,
   * so a page that stays open keeps the build it opened with). So this is
   * written again, as the WHOLE game length (`withLegacyClock`): the clock is
   * continuous now, so that old arithmetic yields exactly the new countdown.
   * Readers here use `lengthMin`; `gameLengthMs` treats a state that has only
   * this field as two real halves, which is what it was when written.
   */
  halfLengthMin?: number;
  /** Game-to-N points; null = time-only format. */
  scoreTarget: number | null;
  timeoutsPerHalf: number;
  /**
   * Minutes of GAME TIME (both halves together) after which the soft cap, and
   * then the hard cap, is due. Optional: an event that does not use time caps
   * stores neither, and so does every game configured before these existed.
   *
   * They are a REMINDER, never a rule the clock enforces. `capDue`
   * (`shared/cap.ts`) says a cap has come round; a human taps to call it,
   * because the horn at a real tournament is blown by staff and software that
   * found out first must not change the score on its own.
   */
  softCapMin?: number;
  hardCapMin?: number;
}

/** Ceiling for a game total, cap-adjusted ones included. Beside the field it
 *  bounds rather than in `worker/validate.ts`, so the cap maths and the
 *  validator cannot disagree about what a legal target is. */
export const MAX_SCORE_TARGET = 30;

export type Period = "pregame" | "h1" | "half" | "h2" | "final";

/**
 * What the OBS overlay renders.
 *
 * "auto" is the default since 2026-08: the overlay derives live/break itself
 * from the period, `finalAt` and the schedule (`shared/overlay-scene.ts`), so
 * a scorekeeper never has to remember to switch - the failure this replaces
 * was a schedule card sitting on stream through a whole game. The other three
 * are MANUAL PINS: they mean "stop deriving, show exactly this", survive
 * everything except the next "Set up game" (which resets to auto), and exist
 * for stoppages and OBS setup. "hidden" is never produced by auto.
 */
export type Scene = "auto" | "live" | "break" | "hidden";

export interface GameTeam {
  /** Team id when picked from the roster; null for ad-hoc names. */
  teamId: string | null;
  name: string;
  short: string;
  color: string;
  logo?: string;
  /** Dark-ground variant of the crest: see `Team.logoDark`. */
  logoDark?: string;
  score: number;
  timeoutsLeft: number;
  /**
   * Roster copied in at setup so overlay and board can resolve scorer names
   * from game state alone. A later roster edit does not rewrite past games.
   */
  roster?: Player[];
}

/**
 * One scored point. `offense` records which team began the point on offense
 * (received the pull), so a break is simply "the team that started on defense
 * scored". Turnovers are never tracked: breaks are derived, which keeps the
 * scorekeeper at one tap per point.
 */
export interface PointEvent {
  /** Scoring team. */
  team: "A" | "B";
  /** Which team began this point on offense. */
  offense: "A" | "B";
  scorerId: string | null;
  assistId: string | null;
  /** Score after this point, for rebuilding a timeline. */
  scoreA: number;
  scoreB: number;
  at: number;
}

/** A break is a point won by the team that started it on defense. */
export function isBreak(p: PointEvent): boolean {
  return p.team !== p.offense;
}

export function breakCount(points: PointEvent[], team: "A" | "B"): number {
  return points.filter((p) => p.team === team && isBreak(p)).length;
}

export interface PlayerTally {
  playerId: string;
  goals: number;
  assists: number;
}

/**
 * The streak the game is on: who took the last points, and how many in a row.
 *
 * `null` for a game with no points yet. Callers decide what counts as a run
 * worth mentioning — two in a row is a coincidence, and this does not pretend
 * to know where the line is.
 */
export function currentRun(points: PointEvent[]): { team: "A" | "B"; length: number } | null {
  const last = points[points.length - 1];
  if (last === undefined) return null;
  let length = 1;
  for (let i = points.length - 2; i >= 0; i -= 1) {
    if (points[i]?.team !== last.team) break;
    length += 1;
  }
  return { team: last.team, length };
}

/**
 * Longer than any real point. A gap this big is a game left open between
 * rounds, a scorekeeper who walked away, or a hand-edited state — not a stat,
 * and putting "47:12" on a broadcast graphic as a point length is worse than
 * saying nothing.
 */
const MAX_POINT_MS = 20 * 60_000;

/**
 * How long one point took, measured from the point before it.
 *
 * `null` for the FIRST point of a game on purpose: there is no earlier
 * timestamp to measure from, and the pull does not happen when the clock
 * starts — teams walk on, captains talk, the disc gets found. Deriving it from
 * the clock would put an invented number on air.
 *
 * Also `null` for a non-positive gap: undoing and re-adding a point lands two
 * timestamps a moment apart, and a clock correction can move `at` backwards.
 */
export function pointLengthMs(points: PointEvent[], index: number): number | null {
  const p = points[index];
  const prev = points[index - 1];
  if (p === undefined || prev === undefined) return null;
  const ms = p.at - prev.at;
  if (ms <= 0 || ms > MAX_POINT_MS) return null;
  return ms;
}

/** The longest point of the game so far. `null` until there are two points. */
export function longestPointMs(points: PointEvent[]): number | null {
  let best: number | null = null;
  for (let i = 1; i < points.length; i += 1) {
    const ms = pointLengthMs(points, i);
    if (ms !== null && (best === null || ms > best)) best = ms;
  }
  return best;
}

/** Goal/assist totals per player, best first. Players with nothing are omitted. */
export function tallyPlayers(points: PointEvent[], team: "A" | "B"): PlayerTally[] {
  const rows = new Map<string, PlayerTally>();
  const bump = (id: string | null, key: "goals" | "assists"): void => {
    if (id === null) return;
    const row = rows.get(id) ?? { playerId: id, goals: 0, assists: 0 };
    row[key] += 1;
    rows.set(id, row);
  };
  for (const p of points) {
    if (p.team !== team) continue;
    bump(p.scorerId, "goals");
    bump(p.assistId, "assists");
  }
  return [...rows.values()].sort(
    (a, b) => b.goals - a.goals || b.assists - a.assists || a.playerId.localeCompare(b.playerId),
  );
}

export interface GameState {
  /** Revision counter: bumps on every action, lets clients drop stale frames. */
  v: number;
  fieldId: FieldId;
  scene: Scene;
  period: Period;
  teamA: GameTeam;
  teamB: GameTeam;
  clock: ClockState;
  config: GameConfig;
  /** Manual banner override, e.g. "SOFT CAP ON". null = auto (game/universe point). */
  banner: string | null;
  /** Link back to the schedule entry (result write-back on final). */
  scheduleGameId: string | null;
  /**
   * SERVER-stamped moment the period entered "final"; absent outside it.
   * The overlay's auto scene holds the final card for a configured window
   * past this, then falls to the schedule - stamped once by the reducer so
   * every overlay instance counts the same window. Optional because states
   * persisted before it existed lack it (auto treats those as expired).
   */
  finalAt?: number;
  /**
   * True once a scorekeeper has run "Set up game". Gates the pregame intro
   * card so an untouched field doesn't sit on a placeholder matchup all day.
   * Optional because states persisted before this field existed lack it.
   */
  configured?: boolean;
  /**
   * Which team starts the NEXT point on offense (receives the pull). Flips
   * automatically after each score: whoever just scored pulls next, so the
   * other team receives. Optional for states saved before point tracking.
   */
  offense?: "A" | "B";
  /**
   * Who received the OPENING pull. The second half swaps relative to this (the
   * team that received to start the game pulls to start H2), so it must be
   * remembered separately from the running `offense`.
   */
  openingOffense?: "A" | "B";
  /** Scored points, oldest first. Drives breaks, player stats and graphics. */
  points?: PointEvent[];
  /**
   * Which time cap a human has CALLED, and therefore which adjustment has
   * already been made to `config.scoreTarget`. Never written by the clock.
   */
  capCalled?: "soft" | "hard";
  /**
   * LEGACY: game time banked in earlier periods, in milliseconds, by the old
   * per-half clock (it restarted from zero at halftime, and the caps needed
   * the first half remembered somewhere). The clock is ONE continuous game
   * clock now and nothing writes this any more; `gameElapsedMs` still adds it
   * so a game that was mid-way through its second half when this changed
   * keeps its time on stream. Cleared when a game goes back to pregame/h1.
   */
  priorMs?: number;
  /**
   * The timeout in progress, or the last one taken: which side called it and
   * WHEN (server time, action-dated like every stamp in here). Stamped by the
   * reducer when a timeout is SPENT, so every overlay, board and phone counts
   * the same 75 seconds down locally from `{team, at}` plus the server skew -
   * the game clock's own rule, and no per-second broadcast. Cleared when that
   * timeout is handed back (a phantom tap, so its countdown was one too), by
   * the next score (play is on, whatever the seconds say) and by a period
   * change. Absent on states from before it existed.
   */
  timeout?: { team: "A" | "B"; at: number };
  /**
   * True once `startSecondHalf` has run for THIS game. It is what tells
   * "entering h2" apart from "returning to h2": the second-half setup (bank
   * the first half, zero the clock, refill timeouts, swap possession from the
   * opening pull) must happen exactly once, and re-running it on the way back
   * from a mis-tapped "final" or "half" wiped the banked first half, reset
   * the stream clock to 0:00 mid-half and handed spent timeouts back.
   * Cleared when the period goes back to pregame/h1, absent on old rows
   * (which at worst reproduces the old behaviour once, mid-deploy).
   */
  secondHalfStarted?: true;
  updatedAt: number;
}

export const DEFAULT_CONFIG: GameConfig = {
  clockMode: "countdown",
  // `DEFAULT_SLOTS.pool` in game-length.ts, which cannot be imported here
  // without a cycle; the setup dialog prefills the real number from the slots.
  lengthMin: 60,
  halfLengthMin: 60,
  scoreTarget: 15,
  timeoutsPerHalf: 2,
};

/**
 * The config with its compatibility field filled: `halfLengthMin` set to the
 * WHOLE game length (see the field's note). Applied to every state message the
 * FieldDO sends and to every config the validator accepts, so a client still
 * on the previous build - an OBS source nobody reloaded, a spectator's stale
 * PWA - renders the same countdown instead of NaN. A state that carries only
 * the old field (persisted before `lengthMin` existed) is left alone: there
 * the number really is a half, and `gameLengthMs` doubles it.
 */
export function withLegacyClock(config: GameConfig): GameConfig {
  const stored: Partial<GameConfig> = config;
  if (stored.lengthMin === undefined) return config;
  if (stored.halfLengthMin === stored.lengthMin) return config;
  return { ...config, halfLengthMin: stored.lengthMin };
}

/**
 * How long a timeout lasts. WFDF rule 6.2: seventy-five seconds. The sideline
 * counts them from the call; the overlay badge and the scorekeeper's row count
 * the same seconds from `GameState.timeout.at`.
 */
export const TIMEOUT_MS = 75_000;

export function defaultTeam(name: string, short: string, color: string): GameTeam {
  return {
    teamId: null,
    name,
    short,
    color,
    score: 0,
    timeoutsLeft: DEFAULT_CONFIG.timeoutsPerHalf,
  };
}

export function initialGameState(fieldId: FieldId): GameState {
  return {
    v: 0,
    fieldId,
    scene: "auto",
    period: "pregame",
    teamA: defaultTeam("Dark", "DRK", "#3b6ef5"),
    teamB: defaultTeam("White", "WHT", "#e14646"),
    clock: { running: false, startedAt: null, accumulatedMs: 0 },
    config: { ...DEFAULT_CONFIG },
    banner: null,
    scheduleGameId: null,
    updatedAt: 0,
  };
}

// ---------- Actions (admin → FieldDO) ----------

export interface SetupPayload {
  teamA: Pick<GameTeam, "teamId" | "name" | "short" | "color" | "logo" | "logoDark" | "roster">;
  teamB: Pick<GameTeam, "teamId" | "name" | "short" | "color" | "logo" | "logoDark" | "roster">;
  config: GameConfig;
  scheduleGameId: string | null;
  /** Which team receives the opening pull. */
  offense?: "A" | "B";
}

/**
 * What every action may carry alongside its own fields.
 *
 * An AGE, never a timestamp. A queued action has to be applied at the moment it
 * was TAKEN — the reducer stamps points and clock transitions with the time it
 * is handed — and a volunteer's phone at a field is regularly minutes off wall
 * clock. Both readings come from that same wrong clock, so the error cancels in
 * the subtraction and only the elapsed measurement, which is reliable, reaches
 * the server. Absent means "now", which is every action sent while online.
 */
interface ActionMeta {
  ageMs?: number;
  /**
   * Client-generated, stable across retries. The FieldDO remembers the ids it
   * has applied, so a POST whose RESPONSE was lost can be sent again without
   * counting the point twice — which is the difference between a queue that
   * survives a dead zone and one that invents scores on the way out of it.
   *
   * It also makes `undo` safe to retry, and `undo` is the action that most
   * needs it: applying one twice silently rolls back a point nobody touched.
   */
  id?: string;
}

export type GameAction = ActionMeta &
  (
  | { type: "score"; team: "A" | "B"; delta: 1 | -1; scorerId?: string | null; assistId?: string | null }
  /** Fill in (or correct) scorer/assist on the most recent point. */
  | { type: "credit"; scorerId: string | null; assistId: string | null }
  /** Manually set which team starts the next point on offense. */
  | { type: "offense"; team: "A" | "B" }
  | { type: "score_set"; a: number; b: number }
  | { type: "timeout"; team: "A" | "B"; delta: 1 | -1 }
  | { type: "clock"; op: "start" | "stop" | "reset" }
  | { type: "clock_set"; ms: number }
  /**
   * Nudge the clock by a signed amount of ELAPSED milliseconds, atomically:
   * running state and startedAt are untouched, so "+1 min" is one action (and
   * one undo entry) instead of a stop/set/start dance. The UI flips the sign
   * in countdown mode so the button always moves the DISPLAYED time.
   */
  | { type: "clock_adjust"; deltaMs: number }
  | { type: "period"; period: Period }
  | { type: "scene"; scene: Scene }
  | { type: "banner"; text: string | null }
  /**
   * Call a time cap: the game total drops to the leader's score plus two
   * (soft) or plus one (hard). One action, so it is one undo entry, and the
   * scorekeeper taps it — the clock only ever offers it. See `shared/cap.ts`.
   */
  | { type: "cap"; cap: "soft" | "hard" }
  | { type: "setup"; payload: SetupPayload }
  | { type: "undo" });

/** Message pushed to every overlay/board socket (and returned by GET state). */
export interface StateMessage {
  type: "state";
  state: GameState;
  /** Server wall clock at send time: clients use it to correct clock skew. */
  now: number;
  /**
   * `state` has had its team ASSETS stripped — see `slimState`. The receiver
   * re-attaches them from the state it already holds (`regraftState`).
   *
   * An explicit flag and not "the logo is missing": a team with no crest is a
   * legitimate state, and `exactOptionalPropertyTypes` means absence is a value
   * here rather than an unknown. A reader that ignores this flag degrades to
   * losing logos, never to showing the wrong one.
   */
  slim?: true;
  /**
   * How many entries the server's undo stack holds. The stack lives in the
   * Durable Object and the reducer has never seen it, so the client cannot
   * derive this — and the heuristic it used instead (points or score present)
   * disabled a perfectly reachable undo after a `score_set` to 0-0. Absent on
   * an old server; the client falls back to the heuristic then.
   */
  undo?: number;
  /**
   * What the next undo would revert: the type of the action that produced the
   * top undo entry. The button on the scorekeeper says "Undo last point", but
   * the stack holds every action — a volunteer who scored, then stopped the
   * clock, then tapped undo was silently restarting the clock. Naming the
   * target turns that misclick into an informed tap. Absent when the stack is
   * empty or against an old server.
   */
  undoKind?: GameAction["type"];
}

// ---------- slim frames ----------

/**
 * A team without the two fields that cannot change inside one configured game.
 *
 * `logo` is a data URL and `roster` is up to two dozen players, so they are
 * almost the whole weight of a state — and they are the only part of it that
 * never moves while a game is being played. Only `setup` replaces a team, and
 * `setup` clears the undo stack and sends a full frame.
 */
export function slimTeam(t: GameTeam): GameTeam {
  const { logo: _logo, logoDark: _logoDark, roster: _roster, ...rest } = t;
  return rest;
}

/** `slimTeam` for both sides. */
export function slimState(g: GameState): GameState {
  return { ...g, teamA: slimTeam(g.teamA), teamB: slimTeam(g.teamB) };
}

/**
 * Re-attach the assets a slim team dropped, from one that still has them.
 *
 * REFUSES when the two are not the same team. Within one configured game they
 * always are, so this guard should never fire — but what it prevents is putting
 * one team's crest on another team's block on a live broadcast, and a missing
 * logo is a far cheaper failure than a wrong one.
 */
export function regraftTeam(slim: GameTeam, from: GameTeam): GameTeam {
  if (slim.teamId !== from.teamId || slim.name !== from.name) return slim;
  return {
    ...slim,
    ...(from.logo !== undefined ? { logo: from.logo } : {}),
    ...(from.logoDark !== undefined ? { logoDark: from.logoDark } : {}),
    ...(from.roster !== undefined ? { roster: from.roster } : {}),
  };
}

/** `regraftTeam` for both sides. */
export function regraftState(slim: GameState, from: GameState): GameState {
  return {
    ...slim,
    teamA: regraftTeam(slim.teamA, from.teamA),
    teamB: regraftTeam(slim.teamB, from.teamB),
  };
}

/**
 * Per-game stats snapshot persisted to the tournament when a game reaches
 * "final". Live `points` only exist inside the FieldDO and are wiped by the
 * next "Set up game", so anything tournament-wide (scorer leaderboards, break
 * totals per team) must read these instead. Rosters are copied in so player
 * ids in `points` stay resolvable even after a team's roster is edited.
 */
export interface GameStats {
  scheduleGameId: string;
  scoreA: number;
  scoreB: number;
  points: PointEvent[];
  teamA: { teamId: string | null; name: string; short: string; roster?: Player[] };
  teamB: { teamId: string | null; name: string; short: string; roster?: Player[] };
  recordedAt: number;
}

// ---------- OBS remote control (bridge tab on the OBS laptop ↔ FieldDO) ----------

/** Snapshot the bridge pushes; volatile, never persisted, never bumps GameState.v. */
export interface ObsStatus {
  /** Bridge tab ↔ local OBS WebSocket connection. */
  connected: boolean;
  streaming: boolean;
  reconnecting: boolean;
  bitrateKbps: number | null;
  droppedPct: number | null;
  fps: number | null;
  cpuPct: number | null;
  currentScene: string | null;
  scenes: string[];
  updatedAt: number;
}

export const OBS_OFFLINE: ObsStatus = {
  connected: false,
  streaming: false,
  reconnecting: false,
  bitrateKbps: null,
  droppedPct: null,
  fps: null,
  cpuPct: null,
  currentScene: null,
  scenes: [],
  updatedAt: 0,
};

/** Admin → bridge, relayed verbatim through the FieldDO. */
export type ObsCommand =
  | { type: "scene"; sceneName: string }
  | { type: "stream"; on: boolean }
  | { type: "refresh" };

/** Pushed to overlay/board/admin sockets alongside StateMessage; overlay ignores it. */
export interface ObsMessage {
  type: "obs";
  status: ObsStatus;
  now: number;
}

/** DO → bridge socket. */
export interface ObsCommandMessage {
  type: "obs-cmd";
  cmd: ObsCommand;
}

// ---------- Clock helpers (pure, used by worker + UI) ----------

export function elapsedMs(clock: ClockState, now: number): number {
  return clock.accumulatedMs + (clock.running && clock.startedAt !== null ? now - clock.startedAt : 0);
}

/**
 * The whole game's playing time, in milliseconds: what the countdown starts at.
 *
 * Read through `Partial` on purpose. A state persisted before `lengthMin`
 * existed carries `halfLengthMin` only, and the undo stack can hand such a
 * snapshot back mid-deploy; two of those halves are the game. The compiled-in
 * default is the last resort, so the bug can never read "NaN:NaN" on stream.
 */
export function gameLengthMs(config: GameConfig): number {
  const stored: Partial<GameConfig> = config;
  const minutes =
    stored.lengthMin ??
    (stored.halfLengthMin !== undefined ? stored.halfLengthMin * 2 : DEFAULT_CONFIG.lengthMin);
  return minutes * 60_000;
}

/** Milliseconds to show on the bug: time left in the GAME in countdown mode
 *  (clamped at 0), time played otherwise. Whole game, not the half: the clock
 *  pauses at the break and resumes, see `GameConfig.lengthMin`. */
export function displayMs(
  state: Pick<GameState, "clock" | "config" | "priorMs">,
  now: number,
): number {
  const e = gameElapsedMs(state, now);
  if (state.config.clockMode === "countdown") return Math.max(0, gameLengthMs(state.config) - e);
  return e;
}

/**
 * How far into the GAME we are, halftime excluded.
 *
 * The clock is continuous (it pauses at halftime rather than restarting), so
 * this is the clock itself - plus whatever the OLD per-half clock banked in
 * `priorMs` for a game that was already past the break when it changed. Time
 * caps are measured against this, and so is the countdown.
 */
export function gameElapsedMs(state: Pick<GameState, "clock" | "priorMs">, now: number): number {
  return (state.priorMs ?? 0) + elapsedMs(state.clock, now);
}

/**
 * Milliseconds left of a timeout, or null when none is running.
 *
 * Null rather than zero once the seconds are up: "no timeout" and "a timeout
 * that just ended" render the same, and a badge that lingers on 0:00 is the
 * kind of graphic that does not clear itself. Capped at the full length so a
 * stamp slightly ahead of this client's corrected clock reads 1:15, not 1:16.
 */
export function timeoutLeftMs(
  timeout: NonNullable<GameState["timeout"]> | undefined,
  now: number,
): number | null {
  if (timeout === undefined) return null;
  const left = timeout.at + TIMEOUT_MS - now;
  if (left <= 0) return null;
  return Math.min(TIMEOUT_MS, left);
}

export function formatClock(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Auto banner from score, target and cap: "UNIVERSE POINT" beats "GAME POINT",
 * and both beat the cap.
 *
 * A called cap says the game is now to the leader plus two, so a game point is
 * usually seconds away and is the more useful thing to put on the stream. The
 * cap only holds the slot until then. English, permanently: this is broadcast.
 */
export function autoBanner(
  state: Pick<GameState, "teamA" | "teamB" | "config" | "period" | "capCalled">,
): string | null {
  if (state.period === "final" || state.period === "pregame") return null;
  const target = state.config.scoreTarget;
  const a = state.teamA.score;
  const b = state.teamB.score;
  if (target !== null) {
    if (a === target - 1 && b === target - 1) return "UNIVERSE POINT";
    if (a === target - 1 || b === target - 1) return "GAME POINT";
  }
  if (state.capCalled === "hard") return "HARD CAP";
  if (state.capCalled === "soft") return "SOFT CAP";
  return null;
}

export const PERIOD_LABEL: Record<Period, string> = {
  pregame: "PRE",
  h1: "H1",
  half: "HT",
  h2: "H2",
  final: "FINAL",
};
